import React, { FC, useCallback } from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { selectCdnImageSettings } from "@/util/selectors";

import { ENV_CONFIG } from "../../config/env";
import useIsSmallScreen from "@/hooks/useIsSmallScreen";
import ProductDetailImage from "./productDetailImage";
import Page from "@/components/page";
import DeliveryAndReturnMobile from "../../components/delivery/deliveryAndReturnInfoMobile";
import View from "../../components/common/view";

import ProductMobileSizes from "./mobileSizes";
import BrandDetails from "./brandDetails";
import PDPHelmet from "./helmet";
import { useRouteSlug } from "@/hooks";
import ProductPriceSection from "./priceSection";
import TopMobileContent from "./topMobileContent";
import PageSections from "./pageSections";
import LoyaltyPointsInfo from "./loyaltyPoints";
import ProductSizeChart from "./sizeChart/sizeChartContent/productSizeChart";
import ProductWebSizes from "@/components/product/webSizes";
import { ProductHighlights, MobileHighlightsWrapper } from "./highlights";
import {
  ProductQuantity,
  ProductQuantityMob
} from "@/components/product/quantity";
import {
  AddToCart,
  MobileAddToCart
} from "@/components/product/addToCartButton";
import ProductShippingOptions from "@/components/product/webSizes/productShippingOptions";
import SuccessAddedToCartMessage from "@/components/product/addToCartButton/successMessage";
import IsNotPriceAvailableMessage from "@/components/product/isNotPriceAvailableMessage";
import ModelDetails from "@/components/product/modelDetails";
import { PaymentOptions, PaymentOptionsMob } from "./payLaterOptions";
import SizeChart from "./sizeChart";
import ScrollToTop from "./scrollToTop";
import ProductDetailsBreadcrumbs from "./breadcrumbs";
import AddToWishlistButton from "@/components/product/addToWishlistButton";
import DialogModal from "../../components/dialogModal";
import ProductErrorInfo from "./productErrorInfo";
import AiGeneratedImageMessage from "./aiGeneratedImageMessage";
import { RelatedProductsWeb, RelatedProductsMob } from "./relatedProducts";
import GlobalShipping from "./globalShipping";
import ExpressDeliveryTimelines from "./expressDelivery";
import NeedHelpWhatsapp from "./needHelpWhatsapp";
import BrandFollowCard from "@/components/product/brandFollowCard/brandFollowCard";
import { isServer } from "@/util";
import { generateSEOProductSchema } from "@/util/seo";

import { ProductDetailsLayoutProps, ProductImageVariant } from "./types";

import "./style.scss";

const ProductDetailsLayout: FC<ProductDetailsLayoutProps> = ({
  topSections,
  firstSections,
  secondSections,
  language,
  productId,
  itemDetailWrapperRef,
  activeSizeType,
  setActiveSizeType,
  selectedSizeFullData,
  setSelectedSizeFullData,
  setShowProductSizeChart,
  setShowShippingOptions,
  setSelectedQty,
  selectedQty,
  onAddToCartClickHandler,
  addToCart,
  getCartData,
  item,
  isSuccessBlockOpen,
  closeSuccessAddedToCartBlock,
  sameSizeInCartCount,
  payLaterOptions,
  deliveryTimelines,
  deliveryScope,
  selectedSizeSource,
  onSizeSelectionHandler,
  openMultipleSizes,
  setOpenMultipleSizes,
  countryId,
  subcategory,
  captionImageURL,
  productSizeChart,
  showProductSizeChart,
  showShippingOptions,
  goToCart,
  alertMessage,
  setAlertMessage,
  isRTL,
  onSizeSelectionModalClose
}) => {
  const { t } = useTranslation("productDetails");
  const { isSmallScreen } = useIsSmallScreen();
  const handleConfirmDialog = useCallback(() => {
    setAlertMessage("");
  }, [setAlertMessage]);
  const routeSlug = useRouteSlug();
  const configCdnImagesSettings = useSelector(selectCdnImageSettings);

  if (!productId) return null;

  // data for SchemaMarkup
  const baseImageUrl =
    configCdnImagesSettings?.mainImageUrl ||
    configCdnImagesSettings?.main ||
    "";
  const originURL =
    !isServer && window.location
      ? window.location.origin
      : ENV_CONFIG.baseURL || "";
  const canonicalUrlBase = `${originURL}/${routeSlug}`;

  const isPriceAvailable = item?.isPriceAvailableInUserCountry;
  const isImageAiGenerated =
    item?.modelProfile?.type === ProductImageVariant.AI;

  return (
    <>
      <PageSections sections={topSections} />
      <Page id="product-detail">
        <ProductDetailsBreadcrumbs />
        <div
          className={cn("product_detail_wrapper", {
            arabic: isRTL
          })}
        >
          <PDPHelmet />
          <div className="quickview_divider flex">
            <View.mobile>
              <ErrorBoundary fallback={null}>
                <TopMobileContent
                  itemDetailWrapperRef={itemDetailWrapperRef}
                  selectedSizeFullData={selectedSizeFullData}
                />
              </ErrorBoundary>
            </View.mobile>

            <ProductDetailImage shareButton thumbLength={6} key={item.id} />
            <div className="item_details_wrapper" ref={itemDetailWrapperRef}>
              <BrandDetails />
              {isPriceAvailable && <ProductPriceSection />}
              <View.mobile>
                <ErrorBoundary fallback={null}>
                  <ProductErrorInfo
                    product={item}
                    selectedSizeFullData={selectedSizeFullData}
                    productSizeChart={productSizeChart}
                    showProductSizeChart={showProductSizeChart}
                    showShippingOptionsModal={() => {
                      setShowShippingOptions(true);
                    }}
                  />
                </ErrorBoundary>
              </View.mobile>

              {!isEmpty(deliveryTimelines) && (
                <ExpressDeliveryTimelines
                  deliveryTimelines={deliveryTimelines}
                  scope={deliveryScope}
                  selectedSizeSource={selectedSizeSource}
                />
              )}

              {isPriceAvailable && isSmallScreen ? (
                <View.mobile>
                  <RelatedProductsMob product={item} />
                  <ErrorBoundary fallback={null}>
                    <ProductMobileSizes
                      activeSizeType={activeSizeType}
                      setActiveSizeType={setActiveSizeType}
                      selectedSizeFullData={selectedSizeFullData}
                      onSizeSelectionHandler={onSizeSelectionHandler}
                      showProductSizeChart={() => setShowProductSizeChart(true)}
                      openMultipleSizes={openMultipleSizes}
                      setOpenMultipleSizes={setOpenMultipleSizes}
                      onSizeSelectionModalClose={onSizeSelectionModalClose}
                    />
                  </ErrorBoundary>
                </View.mobile>
              ) : null}

              {isImageAiGenerated && (
                <View.mobile>
                  <AiGeneratedImageMessage item={item} box />
                </View.mobile>
              )}

              <PageSections sections={firstSections} />
              <GlobalShipping
                globalShipping={item?.highlights?.globalShipping}
              />
              {isPriceAvailable ? (
                <>
                  <View.desktop>
                    <ErrorBoundary fallback={null}>
                      <ProductWebSizes
                        productDetails={item}
                        productSizeChart={productSizeChart}
                        activeSizeType={activeSizeType}
                        setActiveSizeType={setActiveSizeType}
                        selectedSizeFullData={selectedSizeFullData}
                        setSelectedSizeFullData={setSelectedSizeFullData}
                        showProductSizeChart={() =>
                          setShowProductSizeChart(true)
                        }
                        showShippingOptionsModal={() => {
                          setShowShippingOptions(true);
                        }}
                      />
                    </ErrorBoundary>
                  </View.desktop>

                  <div className="action_container mt-4">
                    <View.mobile>
                      <ProductQuantityMob
                        selectedSize={selectedSizeFullData.size}
                        setSelectedQty={setSelectedQty}
                        selectedQty={selectedQty}
                      />
                    </View.mobile>
                    <MobileAddToCart
                      onAddToCartClickHandler={onAddToCartClickHandler}
                      className="d-none-desktop"
                    />

                    <View.desktop element="span">
                      <ProductQuantity
                        product={item}
                        selectedSize={selectedSizeFullData.size}
                        setSelectedQty={setSelectedQty}
                        selectedQty={selectedQty}
                      />

                      <AddToCart
                        product={item}
                        handleAddToCart={() => addToCart(getCartData())}
                        selectedSize={selectedSizeFullData.size}
                      />
                    </View.desktop>
                    <ErrorBoundary fallback={null}>
                      <AddToWishlistButton
                        product={item}
                        selectedSizeFullData={selectedSizeFullData}
                      />
                    </ErrorBoundary>
                    {isImageAiGenerated && (
                      <View.desktop>
                        <AiGeneratedImageMessage item={item} />
                      </View.desktop>
                    )}
                    {item?.modelProfile && !isImageAiGenerated && (
                      <View.desktop>
                        <ModelDetails modelProfile={item.modelProfile} />
                      </View.desktop>
                    )}
                    <SuccessAddedToCartMessage
                      selectedQty={selectedQty}
                      isSuccessBlockOpen={isSuccessBlockOpen}
                      closeSuccessAddedToCartBlock={
                        closeSuccessAddedToCartBlock
                      }
                      sameSizeInCartCount={sameSizeInCartCount}
                    />
                  </div>
                </>
              ) : (
                <IsNotPriceAvailableMessage />
              )}

              {isPriceAvailable && (
                <>
                  {isSmallScreen ? (
                    <PaymentOptionsMob payLaterOptions={payLaterOptions} />
                  ) : (
                    <>
                      <PaymentOptions payLaterOptions={payLaterOptions} />
                      <RelatedProductsWeb product={item} />
                    </>
                  )}
                </>
              )}

              {item?.brand?.id && (
                <BrandFollowCard
                  brandId={item.brand.id}
                  brandName={item.brand.defaultValue || item.brand.label || ""}
                />
              )}

              <PageSections sections={secondSections} />

              <View.mobile className="mobile-view productExtraInfo">
                <MobileHighlightsWrapper />
                <ErrorBoundary fallback={null}>
                  <DeliveryAndReturnMobile />
                </ErrorBoundary>
                <SizeChart setShowProductSizeChart={setShowProductSizeChart} />
                <NeedHelpWhatsapp />
                <LoyaltyPointsInfo />
                <ScrollToTop />
              </View.mobile>

              <View.desktop>
                <ProductHighlights />
              </View.desktop>
            </div>
          </div>
          {showProductSizeChart && (
            <ErrorBoundary fallback={null}>
              <ProductSizeChart
                handleSizeChartPopup={setShowProductSizeChart}
              />
            </ErrorBoundary>
          )}
          {showShippingOptions && (
            <ErrorBoundary fallback={null}>
              <ProductShippingOptions
                hideShippingOptionsModal={() => {
                  setShowShippingOptions(false);
                }}
                isModalShow={showShippingOptions}
              />
            </ErrorBoundary>
          )}
        </div>
        <DialogModal
          showModal={Boolean(alertMessage)}
          titleText={t("alert")}
          bodyText={alertMessage}
          confirmButtonText={t("Ok")}
          onConfirm={handleConfirmDialog}
          onCancel={goToCart}
          cancelButtonText={t("viewCart")}
        />
      </Page>
    </>
  );
};

export default ProductDetailsLayout;
